import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Lightbox from '../components/Lightbox';
import { getStorytelling } from '../lib/cms';
import { imageUrl } from '../lib/images';

export default function StorytellingPage() {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    async function loadData() {
      const data = await getStorytelling();
      setStories(data);
      setLoading(false);
    }
    loadData();
  }, []);

  const openLightbox = (imgSrc, altText) => {
    setLightboxImage({ image: imgSrc, title: altText });
    setLightboxOpen(true);
  };

  return (
    <>
      <SEO
        title="Storytelling – Fortællingen bag billederne"
        description="Oplev fortællingerne bag billederne hos Lunde Media. Billeder der vækker minder og følelser – fra personlige frierier til autentiske livsøjeblikke."
      />

      {/* Hero Section */}
      <section className="relative min-h-[60vh] sm:min-h-[68vh] flex items-center justify-center pt-28 pb-16 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={imageUrl(
              '/_assets/472331762-1068515591958554-2413136772439242105-n.jpg',
              'width=1920,quality=85,format=auto'
            )}
            alt="Brud der kaster smutsten på stranden"
            className="w-full h-full object-cover filter brightness-[0.45] contrast-[1.05]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0C0C0D] via-stone-950/60 to-transparent" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-8 text-center space-y-6">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-sans font-medium text-white tracking-tight leading-tight max-w-4xl mx-auto">
            Storytelling
          </h1>

          <p className="text-base sm:text-lg md:text-xl text-stone-200 font-sans max-w-2xl mx-auto leading-relaxed">
            Et billede der ligger op til en fortælling, en fortælling der vækker minder, minder der vækker følelser.
          </p>

          <div className="pt-2 flex flex-wrap items-center justify-center gap-4">
            <a
              href="#historier"
              className="inline-flex items-center justify-center gap-2 px-7 py-3 bg-white hover:bg-stone-100 text-[#0C0C0D] font-medium text-sm tracking-wide transition-all shadow-md"
            >
              <span>Læs historierne</span>
              <i className="fa-solid fa-arrow-down text-xs" aria-hidden="true" />
            </a>
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center gap-2 px-7 py-3 bg-transparent hover:bg-white/10 border border-white/30 text-white font-medium text-sm tracking-wide transition-all"
            >
              <span>Fortæl jeres historie</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Philosophy Introduction */}
      <section className="py-16 md:py-20 bg-stone-50 border-b border-stone-200 text-stone-800">
        <div className="max-w-4xl mx-auto px-6 md:px-8 text-center space-y-6">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-normal text-stone-900 leading-snug">
            »Dét er hvad jeg ønsker i mit virke:<br />Fortællingen, minderne og følelserne.«
          </h2>
        </div>
      </section>

      {/* Stories Section */}
      <section id="historier" className="py-20 md:py-28 bg-white text-[#141416] border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 md:px-8 space-y-20 md:space-y-24">
          {loading ? (
            <div className="text-center py-20 text-stone-500">
              <i className="fa-solid fa-circle-notch fa-spin text-2xl text-[#A67C46] mb-4" />
              <p>Henter fortællinger...</p>
            </div>
          ) : (
            stories.map((story, idx) => {
              const mainImage = story.billeder?.[0] || '/_assets/472331762-1068515591958554-2413136772439242105-n.jpg';
              return (
                <div
                  key={story.id || idx}
                  className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center"
                >
                  <div
                    onClick={() => openLightbox(mainImage, story.titel)}
                    className="md:col-span-5 aspect-[4/5] rounded-none overflow-hidden border border-zinc-200 shadow-lg relative group cursor-pointer bg-stone-100"
                  >
                    <img
                      src={imageUrl(mainImage, 'width=800,quality=85,format=auto')}
                      alt={story.titel || 'Bryllupsbillede med storytelling'}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="md:col-span-7 space-y-6">
                    <h2 className="text-3xl sm:text-4xl font-serif text-[#141416] leading-tight">
                      {story.titel}
                    </h2>
                    <div
                      className="space-y-4 text-[#3A3836] text-sm md:text-base leading-relaxed font-sans [&>p]:leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: story.fortaelling }}
                    />
                    <div className="pt-2">
                      <Link
                        to="/kontakt"
                        className="cta-btn inline-flex items-center gap-2 px-7 py-3.5 bg-[#141416] hover:bg-black text-white text-sm font-medium transition-colors"
                      >
                        Få jeres historie fortalt <i className="fa-solid fa-arrow-right text-xs" />
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-24 bg-white border-t border-stone-200 text-center">
        <div className="max-w-4xl mx-auto px-6 md:px-8 space-y-6">
          <h2 className="text-3xl sm:text-4xl font-serif font-medium text-stone-900 tracking-tight">
            Har I en særlig fortælling, I vil have foreviget?
          </h2>
          <p className="text-base sm:text-lg text-stone-600 max-w-2xl mx-auto leading-relaxed">
            Uanset om det gælder jeres bryllup, en stor begivenhed eller et familieminde, 
            tager vi en indledende snak om de detaljer og historier, der betyder mest for jer.
          </p>
          <div className="pt-4 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/kontakt"
              className="cta-btn inline-flex items-center justify-center px-8 py-3.5 bg-[#141416] hover:bg-black text-white font-semibold text-sm tracking-wide transition-all shadow-md"
            >
              Book et uforpligtende møde
            </Link>
            <a
              href="tel:26368306"
              className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-stone-100 hover:bg-stone-200 text-stone-900 font-medium text-sm tracking-wide transition-all border border-stone-300"
            >
              <i className="fa-solid fa-phone text-xs text-[#A67C46]" aria-hidden="true" />
              <span>Ring 26 36 83 06</span>
            </a>
          </div>
        </div>
      </section>

      {/* Lightbox for high-res view */}
      {lightboxOpen && lightboxImage && (
        <Lightbox
          images={[lightboxImage]}
          currentIndex={0}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </>
  );
}
